import {
  getAllWarehouses,
  getDatabaseFilters,
  getSchemaFilters,
  getGovernanceRoles,
  getGovernanceUsers,
  getDatabricksAccessRuleOptions,
} from "@/helpers/apis";

export type EntityType =
  | "warehouse"
  | "database"
  | "schema"
  | "role"
  | "user"
  | "account"
  | "workspace";

// Map entity/condition types to data entity types
export const getDataEntityType = (entityType?: string): EntityType | undefined => {
  if (!entityType) return undefined;

  const typeMap: Record<string, EntityType> = {
    warehouse: "warehouse",
    database: "database",
    schema: "schema",
    role: "role",
    user: "user",
    warehouse_name: "warehouse",
    query_warehouse_name: "warehouse",
    database_name: "database",
    schema_name: "schema",
    query_role: "role",
    // Databricks
    account: "account",
    workspace: "workspace",
  };

  return typeMap[entityType.toLowerCase()];
};

export const fetchWarehouses = async (searchTerm: string, pageParam: number) => {
  const response = await getAllWarehouses({
    search: searchTerm || undefined,
    page: String(pageParam),
    size: "50",
  });
  return {
    items: (response?.items ?? []).map(warehouse => ({
      label: warehouse.warehouse_name,
      value: warehouse.warehouse_name,
    })),
    pages: response?.pages ?? 1,
    currentPage: pageParam,
  };
};

export const fetchDatabases = async (searchTerm: string, pageParam: number) => {
  const response = await getDatabaseFilters({
    prefix: searchTerm || undefined,
    page: pageParam,
    size: 50,
  });
  const uniqueLabels = Array.from(
    new Set(
      (response?.items ?? [])
        .map(item => item.label)
        .filter((label): label is string => Boolean(label))
    )
  );
  return {
    items: uniqueLabels.map(value => ({ label: value, value })),
    pages: response?.pages ?? 1,
    currentPage: pageParam,
  };
};

export const fetchSchemas = async (searchTerm: string, pageParam: number) => {
  const response = await getSchemaFilters({
    prefix: searchTerm || undefined,
    page: pageParam,
    size: 50,
  });
  const uniqueLabels = Array.from(
    new Set(
      (response?.items ?? [])
        .map(item => item.label)
        .filter((label): label is string => Boolean(label))
    )
  );
  return {
    items: uniqueLabels.map(value => ({ label: value, value })),
    pages: response?.pages ?? 1,
    currentPage: pageParam,
  };
};

export const fetchRoles = async (searchTerm: string, pageParam: number) => {
  const response = await getGovernanceRoles({
    search: searchTerm || undefined,
    page: pageParam || 0,
    size: 50,
  });
  return {
    items: (response?.items ?? []).map(role => ({
      label: role.role_name,
      value: role.role_name,
    })),
    pages: response?.pages ?? 1,
    currentPage: pageParam,
  };
};

export const fetchUsers = async (searchTerm: string, pageParam: number) => {
  const response = await getGovernanceUsers({
    search: searchTerm || undefined,
    page: pageParam || 1,
    size: 50,
  });
  return {
    items: (response?.items ?? []).map(user => ({
      label: user.user_name,
      value: user.user_name,
    })),
    pages: response?.pages ?? 1,
    currentPage: pageParam,
  };
};

// The Databricks access-rule options endpoint returns the full catalog in one
// call (no server pagination), so both fetchers request it once and filter
// client-side, returning a single page.
const matchesSearch = (haystack: string, searchTerm: string) =>
  !searchTerm || haystack.toLowerCase().includes(searchTerm.toLowerCase());

export const fetchDatabricksAccounts = async (searchTerm: string, pageParam: number) => {
  const response = await getDatabricksAccessRuleOptions();
  const items = (response?.accounts ?? [])
    .filter(account => matchesSearch(account.account_id, searchTerm))
    .map(account => ({
      label: `${account.account_id} (${account.workspace_count} workspace${
        account.workspace_count === 1 ? "" : "s"
      })`,
      value: account.account_id,
    }));
  return { items, pages: 1, currentPage: pageParam };
};

export const fetchDatabricksWorkspaces = async (searchTerm: string, pageParam: number) => {
  const response = await getDatabricksAccessRuleOptions();
  const items = (response?.workspaces ?? [])
    .filter(
      ws =>
        matchesSearch(ws.workspace_name, searchTerm) || matchesSearch(ws.workspace_id, searchTerm)
    )
    .map(ws => ({
      // Rule matches workspace_id, so that is the stored value; the human name
      // (with id) is shown to the admin.
      label:
        ws.workspace_name === ws.workspace_id
          ? ws.workspace_id
          : `${ws.workspace_name} (${ws.workspace_id})`,
      value: ws.workspace_id,
    }));
  return { items, pages: 1, currentPage: pageParam };
};
